/**
 * Profile import — parse + validate the ``meridian_profile_export`` wrapper
 * (ADR-033 §11.2; REQ-UI-082). The inverse of :file:`profiles-export.ts`.
 *
 * Import is a thin, pure parse/validate layer in front of the existing
 * Profile editor: :func:`parseProfileImport` reads a user-selected JSON file's
 * text and, if it is a well-formed export wrapper this client understands,
 * hands back the inner ``{ name, scopeAtExport, document }`` so the caller can
 * seed the editor's **New** mode (the editor's Identity section IS the
 * §11.2 "prompt for scope + name"; its Save IS the ``POST /api/profiles``;
 * its 409 handling IS the "rename-or-cancel, re-POST on rename"). The BFF
 * re-validates the document authoritatively on POST (422) — the client check
 * deliberately covers only the **wrapper shape + version + presence**, not
 * full ``ProfileDocumentV1`` field validation (CLAUDE.md §4.4 — no duplicated
 * logic; the BFF is the source of truth).
 *
 * The function is **pure and total**: it never throws (a malformed file is a
 * normal, expected input, not an exception) and returns a discriminated
 * :type:`ImportParseResult` so the caller surfaces the first failure's
 * ``error`` string inline (REQ-UI-037 — clear, never leaking internals).
 *
 * Cross-references:
 *   - REQ-UI-082 (import a Profile from a user-selected JSON file).
 *   - ADR-033 §11.2 (validate wrapper + document; prompt scope + name).
 *   - profiles-export.ts (the wrapper this validates — ``EXPORT_FORMAT_VERSION``).
 *   - preferences/default-working-state.ts (the current inner schema version).
 */
import type { ProfileDocument } from "@/auth/session-types";
import { buildDefaultWorkingState } from "@/preferences/default-working-state";
import { EXPORT_FORMAT_VERSION } from "@/profiles/profiles-export";

/**
 * The current ``profile_schema_version`` this client understands. Derived
 * from the default working-state document rather than a re-declared literal so
 * it tracks the generated ``ProfileDocumentV1`` schema automatically: when the
 * inner version bumps, ``buildDefaultWorkingState`` bumps with it and this
 * follows. An import carrying a **newer** inner version is rejected up front so
 * the editor only ever renders a document the client can display (the BFF also
 * 422s a newer version on POST as defence-in-depth — feature-spec §11.2).
 */
export const CURRENT_PROFILE_SCHEMA_VERSION: number =
  buildDefaultWorkingState().profile_schema_version;

/**
 * The outcome of parsing an import file. ``ok: true`` carries the inner
 * Profile ready to seed the editor's New mode; ``ok: false`` carries a single
 * user-facing ``error`` string describing the first validation failure.
 */
export type ImportParseResult =
  | {
      readonly ok: true;
      /** The exported Profile name — prefilled into the editor's name field
       *  (the user may edit it before Save). */
      readonly name: string;
      /** The scope the Profile had when exported — informational; the editor
       *  defaults the scope picker to it (admins) but the user confirms /
       *  changes it, and Global stays admin-gated. */
      readonly scopeAtExport: "global" | "user";
      /** The Profile document to seed the editor draft with. Validated only
       *  for presence here; the BFF re-validates every field on POST. */
      readonly document: ProfileDocument;
    }
  | { readonly ok: false; readonly error: string };

/** Narrow an unknown value to a non-null plain object for field probing. */
function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Parse + validate the text of a user-selected import file (REQ-UI-082;
 * ADR-033 §11.2). Pure and total — never throws.
 *
 * Checks run **in order**, returning the first failure's message:
 *  1. The text is valid JSON.
 *  2. A ``meridian_profile_export`` object is present, and its
 *     ``export_format_version`` equals :data:`EXPORT_FORMAT_VERSION`. A
 *     newer/unknown envelope version ⇒ "exported from a newer version".
 *  3. ``profile`` is an object whose ``document`` is an object and whose
 *     ``name`` is a non-empty (after-trim) string.
 *  4. ``profile.profile_schema_version`` is a number ``<=``
 *     :data:`CURRENT_PROFILE_SCHEMA_VERSION`. A newer inner version ⇒
 *     "this profile was exported from a newer version".
 *
 * Full ``ProfileDocumentV1`` field validation is intentionally **not** done
 * here — the BFF re-validates on POST (422). On success the document is
 * returned typed as :type:`ProfileDocument`; the editor previews it and the
 * BFF is the authority on its validity.
 */
export function parseProfileImport(fileText: string): ImportParseResult {
  // 1. JSON parses.
  let parsed: unknown;
  try {
    parsed = JSON.parse(fileText);
  } catch {
    return {
      ok: false,
      error: "This file isn't valid JSON. Choose a profile export file.",
    };
  }

  // 2a. Envelope present.
  if (!isObject(parsed) || !isObject(parsed.meridian_profile_export)) {
    return {
      ok: false,
      error: "This file isn't a Meridian profile export.",
    };
  }
  const envelope = parsed.meridian_profile_export;

  // 2b. Envelope version known. A newer/unknown version means a format this
  // client can't read — distinct from a newer inner document version (4).
  if (envelope.export_format_version !== EXPORT_FORMAT_VERSION) {
    return {
      ok: false,
      error: "This file was exported from a newer version of Meridian.",
    };
  }

  // 3. profile.document present (object) + profile.name a non-empty string.
  if (!isObject(envelope.profile)) {
    return {
      ok: false,
      error: "This profile export is missing its profile data.",
    };
  }
  const profile = envelope.profile;
  if (!isObject(profile.document)) {
    return {
      ok: false,
      error: "This profile export is missing its profile data.",
    };
  }
  if (typeof profile.name !== "string" || profile.name.trim().length === 0) {
    return {
      ok: false,
      error: "This profile export is missing a profile name.",
    };
  }

  // 4. Inner schema version is a number this client understands (<= current).
  // Reject a NEWER inner version up front so the editor only ever renders a
  // document the client can display (feature-spec §11.2).
  const innerVersion = profile.profile_schema_version;
  if (typeof innerVersion !== "number" || Number.isNaN(innerVersion)) {
    return {
      ok: false,
      error: "This file isn't a Meridian profile export.",
    };
  }
  if (innerVersion > CURRENT_PROFILE_SCHEMA_VERSION) {
    return {
      ok: false,
      error: "This profile was exported from a newer version of Meridian.",
    };
  }

  // The export scope is informational; default it to "user" when the file
  // carries an unexpected value so a malformed-but-otherwise-valid wrapper
  // still imports into the safe (always-allowed) scope.
  const scopeAtExport: "global" | "user" = profile.scope_at_export === "global" ? "global" : "user";

  return {
    ok: true,
    name: profile.name,
    scopeAtExport,
    // Presence-checked above; the BFF re-validates every field on POST.
    document: profile.document as ProfileDocument,
  };
}
